import { useEffect, useMemo, useState } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { findAthlete } from '@/data/sampleAthletes'
import { useAthlete } from '@/hooks/useAthlete'
import { useAthleteProgram } from '@/hooks/usePrograms'
import { useMyProgram } from '@/hooks/useMyProgram'
import { useAthleteAccess, useAccessMutations } from '@/hooks/useAccessControl'
import { useSession } from '@/lib/session'
import { useExerciseHistory, useSaveSet, useSessionSets, isRealAthleteId } from '@/hooks/useSetLogs'
import { useExerciseLibrary } from '@/hooks/useExerciseLibrary'
import { useBilling } from '@/hooks/useBilling'
import { EditAthleteDialog } from '@/components/EditAthleteDialog'
import { groupSupersets } from '@/lib/supersets'
import { parseWeight } from '@/lib/parseWeight'
import { useDayCompletions, useToggleDayComplete, dayKey } from '@/hooks/useDayCompletions'
import { TestingPanel } from '@/components/TestingPanel'
import { useLastTestDate } from '@/hooks/useTestingResults'
import { testingDue } from '@/lib/testingAnalytics'
import { exportProgramToExcel } from '@/lib/exportProgram'
import type { Program, ProgramExercise, ProgramOverview } from '@/types'

/** Athlete's paused screen — offers Subscribe / Manage billing when relevant. */
function PausedBody({
  billingRequired,
  subscriptionStatus,
}: {
  billingRequired?: boolean
  subscriptionStatus?: string | null
}) {
  const { busy, error, subscribe, manage } = useBilling()
  const hadSub = Boolean(subscriptionStatus && subscriptionStatus !== 'active')
  // Paying, but paused by the coach — offering "Subscribe" here would invite a
  // second subscription.
  const payingButPaused = subscriptionStatus === 'active' || subscriptionStatus === 'trialing'
  return (
    <div className="flex flex-1 flex-col items-center justify-center gap-4 p-8 text-center">
      <span className="text-4xl">🔒</span>
      <p className="font-condensed text-2xl font-bold">Access paused</p>
      {billingRequired && payingButPaused ? (
        <>
          <p className="max-w-sm text-sm text-muted">
            Your subscription is active, but your coach has paused your program. Please contact
            your coach to reactivate it.
          </p>
          <button onClick={manage} disabled={busy} className="text-sm text-muted underline underline-offset-4 hover:text-gold">
            Manage billing
          </button>
          {error && <p className="text-sm text-danger">{error}</p>}
        </>
      ) : billingRequired ? (
        <>
          <p className="max-w-sm text-sm text-muted">
            {hadSub
              ? 'Your subscription is no longer active. Restart it to unlock your program.'
              : 'Subscribe to unlock your training program.'}
          </p>
          <Button size="lg" disabled={busy} onClick={subscribe}>
            {busy ? 'Opening…' : 'Subscribe — weekly'}
          </Button>
          {hadSub && (
            <button onClick={manage} disabled={busy} className="text-sm text-muted underline underline-offset-4 hover:text-gold">
              Manage billing
            </button>
          )}
          {error && <p className="text-sm text-danger">{error}</p>}
        </>
      ) : (
        <p className="max-w-sm text-sm text-muted">
          Your access is currently on hold. Please contact your coach to reactivate your program.
        </p>
      )}
    </div>
  )
}
import { cn } from '@/lib/cn'
import { Button } from '@/components/ui/Button'

function OverviewPanel({ overview }: { overview: ProgramOverview }) {
  const [open, setOpen] = useState(false)
  return (
    <div className="mb-4 overflow-hidden rounded-card border border-data/25 bg-data/[.06]">
      <button
        onClick={() => setOpen((v) => !v)}
        className="flex w-full items-center justify-between px-4 py-3 text-left"
      >
        <span className="font-condensed text-sm font-bold uppercase tracking-[0.15em] text-data">
          ⓘ Program Overview & Pain Rules
        </span>
        <span className={cn('text-data transition-transform', open && 'rotate-180')}>▾</span>
      </button>
      {open && (
        <div className="space-y-3 px-4 pb-4 text-[13px] leading-relaxed animate-fade-in">
          {overview.presentation && (
            <p><span className="font-semibold text-text">Presentation: </span><span className="text-muted">{overview.presentation}</span></p>
          )}
          {overview.goal && (
            <p><span className="font-semibold text-text">Goal: </span><span className="text-muted">{overview.goal}</span></p>
          )}
          {overview.frequency && (
            <p><span className="font-semibold text-text">Frequency: </span><span className="text-muted">{overview.frequency}</span></p>
          )}
          {overview.rules && overview.rules.length > 0 && (
            <div>
              <p className="mb-1 font-condensed text-[11px] uppercase tracking-[0.2em] text-danger">Pain rules</p>
              <ul className="list-disc space-y-1 pl-4 text-muted marker:text-danger">
                {overview.rules.map((r, i) => <li key={i}>{r}</li>)}
              </ul>
            </div>
          )}
        </div>
      )}
    </div>
  )
}

export default function AthleteWorkout() {
  const { id = '' } = useParams()
  const navigate = useNavigate()
  const { role, signOut, athleteId: myId } = useSession()
  const sample = findAthlete(id)
  const isSelf = role === 'athlete' && myId === id && !sample
  const isCoachViewing = !sample && !isSelf

  // Self: server-filtered (only unlocked weeks). Coach: full program + identity.
  const mineQ = useMyProgram(isSelf)
  const coachProgramQ = useAthleteProgram(id, isCoachViewing)
  const coachAthleteQ = useAthlete(id, isCoachViewing)

  const paused = isSelf && mineQ.data?.found === true && mineQ.data.access === 'paused'

  const program: Program | null = sample?.program
    ?? (isSelf
      ? mineQ.data?.hasProgram
        ? {
            id: mineQ.data.programId ?? 'me',
            name: mineQ.data.name ?? 'Program',
            source: 'import',
            overview: mineQ.data.overview ?? undefined,
            weeks: mineQ.data.weeks ?? [],
          }
        : null
      : coachProgramQ.data?.program ?? null)

  const athleteName = sample?.name
    ?? (isSelf ? mineQ.data?.name ?? 'Your Program' : coachAthleteQ.data?.name ?? 'Athlete')
  const totalWeeks = program?.weeks.length ?? (isSelf ? mineQ.data?.totalWeeks ?? 0 : 0)
  // Coaches see everything; athletes only up to their unlocked week.
  const unlockedWeek = isSelf ? mineQ.data?.unlockedWeek ?? totalWeeks : totalWeeks
  const nextUnlockAt = isSelf ? mineQ.data?.nextUnlockAt : undefined
  const defaultWeek = sample?.currentWeek ?? (isSelf ? unlockedWeek : coachProgramQ.data?.currentWeek ?? 1)

  const [selectedWeek, setSelectedWeek] = useState<number | null>(null)
  const selWeek = selectedWeek ?? defaultWeek
  const week = useMemo(
    () => program?.weeks.find((w) => w.week === selWeek) ?? program?.weeks[program.weeks.length - 1],
    [program, selWeek]
  )

  const [dayIndex, setDayIndex] = useState(0)
  const [doneByDay, setDoneByDay] = useState<Record<number, Set<number>>>({})
  const [editDetails, setEditDetails] = useState(false)

  // Persisted day completions — the real record of what's been finished.
  // MUST be called before the early returns below, or the hook count changes
  // between renders once the program loads and React throws.
  const completionsQ = useDayCompletions(id, program?.id)
  const toggleDayComplete = useToggleDayComplete()
  const [tab, setTab] = useState<'program' | 'testing'>('program')
  const lastTestQ = useLastTestDate(id)
  const testDue = testingDue(lastTestQ.data)

  const pickWeek = (w: number) => {
    setSelectedWeek(w)
    setDayIndex(0)
    // Ticks are keyed by day index, so without this Week 1 Day 1's ticks (and
    // progress bar) showed up on Week 2 Day 1.
    setDoneByDay({})
  }

  if (paused) {
    return (
      <div className="flex min-h-full flex-col">
        <header className="sticky top-0 z-20 flex items-center gap-3 border-b border-gold/25 bg-surface/95 px-4 py-3 backdrop-blur">
          <h1 className="flex-1 truncate font-display text-xl tracking-wide">From Zero</h1>
          <button
            onClick={async () => { await signOut(); navigate('/login') }}
            className="font-condensed text-[11px] uppercase tracking-[0.15em] text-muted hover:text-gold"
          >
            Sign out
          </button>
        </header>
        <PausedBody
          billingRequired={mineQ.data?.billingRequired}
          subscriptionStatus={mineQ.data?.subscriptionStatus}
        />
      </div>
    )
  }

  if (!program || !week) {
    const loading = isSelf ? mineQ.isLoading : isCoachViewing && (coachProgramQ.isLoading || coachAthleteQ.isLoading)
    const known = Boolean(sample || coachAthleteQ.data || (isSelf && mineQ.data?.found))
    return (
      <div className="flex min-h-full flex-col">
        <header className="sticky top-0 z-20 flex items-center gap-3 border-b border-gold/25 bg-surface/95 px-4 py-3 backdrop-blur">
          <button onClick={() => navigate('/')} className="text-gold hover:-translate-x-0.5" aria-label="Back">‹</button>
          <h1 className="truncate font-display text-xl leading-none tracking-wide">
            {loading ? 'Loading…' : athleteName}
          </h1>
        </header>
        <main className="mx-auto w-full max-w-2xl px-5 pt-5">
        {!isSelf && lastTestQ.isSuccess && testDue.due && (
          <button
            onClick={() => setTab('testing')}
            className="mb-4 flex w-full items-center gap-3 rounded-card border border-gold/40 bg-gold/10 px-4 py-2.5 text-left"
          >
            <span className="text-lg">⚠</span>
            <span className="flex-1">
              <span className="block font-condensed text-sm font-bold uppercase tracking-[0.12em] text-gold">
                Testing due
              </span>
              <span className="block text-xs text-muted">
                {testDue.neverTested
                  ? 'This athlete has never been tested'
                  : `Last tested ${testDue.days} days ago`}
              </span>
            </span>
            <span className="font-condensed text-[11px] uppercase tracking-[0.15em] text-gold">View →</span>
          </button>
        )}
        {/* Program / VALD testing */}
        <div className="mb-5 inline-flex gap-1 rounded-full border border-border bg-surface p-1">
          {(['program', 'testing'] as const).map((t) => (
            <button
              key={t}
              onClick={() => setTab(t)}
              className={cn(
                'rounded-full px-4 py-1.5 font-condensed text-sm font-semibold uppercase tracking-wide transition-all',
                tab === t ? 'gold-gradient text-black' : 'text-muted hover:text-text'
              )}
            >
              {t === 'program' ? 'Program' : 'Testing'}
            </button>
          ))}
        </div>
          {tab === 'testing' && <TestingPanel athleteId={id} isCoach={!isSelf} />}
        </main>
        {tab === 'program' && (
        <div className="flex flex-1 flex-col items-center justify-center gap-4 p-8 text-center">
          {loading ? (
            <p className="text-muted">Loading…</p>
          ) : known ? (
            <>
              <p className="font-condensed text-2xl font-bold">{athleteName}</p>
              <p className="max-w-sm text-sm text-muted">
                No program assigned yet. Build or import a program and assign it to this athlete and
                their sessions appear here.
              </p>
              <Button onClick={() => navigate('/coach/builder')}>Build / import a program</Button>
            </>
          ) : (
            <>
              <p className="text-muted">Athlete not found.</p>
              <Button variant="secondary" onClick={() => navigate('/')}>Back to search</Button>
            </>
          )}
        </div>
        )}
      </div>
    )
  }

  const day = week.days[dayIndex]
  const savedDone = completionsQ.data?.done ?? new Set<string>()
  const dayDone = savedDone.has(dayKey(week.week, dayIndex + 1))
  const doneSet = doneByDay[dayIndex] ?? new Set<number>()
  const trainable = day.exercises.filter((e) => !e.is_warmup)
  const doneCount = [...doneSet].filter((i) => !day.exercises[i]?.is_warmup).length
  const progress = trainable.length ? Math.round((doneCount / trainable.length) * 100) : 0

  const toggleDone = (exIndex: number) => {
    setDoneByDay((prev) => {
      const next = new Set(prev[dayIndex] ?? [])
      next.has(exIndex) ? next.delete(exIndex) : next.add(exIndex)
      return { ...prev, [dayIndex]: next }
    })
  }

  return (
    <div className="flex min-h-full flex-col">
      {/* Top bar */}
      <header className="sticky top-0 z-20 flex items-center gap-3 border-b border-gold/25 bg-surface/95 px-4 py-3 backdrop-blur">
        {role !== 'athlete' && (
          <button
            onClick={() => navigate('/')}
            className="text-gold transition-transform hover:-translate-x-0.5"
            aria-label="Back"
          >
            ‹
          </button>
        )}
        <div className="min-w-0 flex-1">
          <h1 className="truncate font-display text-xl leading-none tracking-wide">{athleteName}</h1>
          <p className="truncate text-xs text-muted">{program.name}</p>
        </div>
        <span className="font-condensed text-xs uppercase tracking-[0.2em] text-muted">
          Week {week.week}
        </span>
        {role === 'athlete' && (
          <button
            onClick={async () => {
              await signOut()
              navigate('/login')
            }}
            className="font-condensed text-[11px] uppercase tracking-[0.15em] text-muted hover:text-gold"
          >
            Sign out
          </button>
        )}
      </header>

      {/* Progress bar */}
      <div className="h-[3px] w-full bg-border">
        <div className="h-full gold-gradient transition-all duration-300" style={{ width: `${progress}%` }} />
      </div>

      <main className="mx-auto w-full max-w-2xl flex-1 px-4 pb-28 pt-5">
        {!isSelf && lastTestQ.isSuccess && testDue.due && (
          <button
            onClick={() => setTab('testing')}
            className="mb-4 flex w-full items-center gap-3 rounded-card border border-gold/40 bg-gold/10 px-4 py-2.5 text-left"
          >
            <span className="text-lg">⚠</span>
            <span className="flex-1">
              <span className="block font-condensed text-sm font-bold uppercase tracking-[0.12em] text-gold">
                Testing due
              </span>
              <span className="block text-xs text-muted">
                {testDue.neverTested
                  ? 'This athlete has never been tested'
                  : `Last tested ${testDue.days} days ago`}
              </span>
            </span>
            <span className="font-condensed text-[11px] uppercase tracking-[0.15em] text-gold">View →</span>
          </button>
        )}
        {/* Program / VALD testing */}
        <div className="mb-5 inline-flex gap-1 rounded-full border border-border bg-surface p-1">
          {(['program', 'testing'] as const).map((t) => (
            <button
              key={t}
              onClick={() => setTab(t)}
              className={cn(
                'rounded-full px-4 py-1.5 font-condensed text-sm font-semibold uppercase tracking-wide transition-all',
                tab === t ? 'gold-gradient text-black' : 'text-muted hover:text-text'
              )}
            >
              {t === 'program' ? 'Program' : 'Testing'}
            </button>
          ))}
        </div>

        {tab === 'testing' ? (
          <TestingPanel athleteId={id} isCoach={!isSelf} />
        ) : (
        <>
        {/* Coach management (access + week unlock) */}
        {isCoachViewing && (
          <>
            <div className="mb-3 flex flex-wrap justify-end gap-2">
              <Button size="sm" variant="ghost" onClick={() => setEditDetails(true)} title="Edit name, email, or add a login">
                ⚙ Details
              </Button>
              <Button
                size="sm"
                variant="ghost"
                onClick={() => navigate(`/coach/builder?assign=${id}`)}
                title="Assign a different / new program to this athlete"
              >
                + New program
              </Button>
              <Button variant="secondary" size="sm" onClick={() => navigate(`/athlete/${id}/report`)}>
                📄 Parent report
              </Button>
              <Button
                variant="secondary"
                size="sm"
                title="Download the full program as an Excel spreadsheet (e.g. for parents)"
                onClick={() =>
                  exportProgramToExcel(program, coachAthleteQ.data?.name ?? 'Athlete').catch((e) =>
                    alert('Couldn’t export: ' + (e as Error).message)
                  )
                }
              >
                ⬇ Export to Excel
              </Button>
              <Button
                size="sm"
                variant="secondary"
                onClick={() => navigate(`/coach/builder?edit=${program.id}&back=/athlete/${id}`)}
              >
                ✎ Edit program
              </Button>
            </div>
            <CoachControls athleteId={id} onWeekJump={pickWeek} />
          </>
        )}

        {/* Week header */}
        <div className="mb-4 flex items-end gap-3">
          <span className="font-display text-5xl leading-none text-gold-gradient">{week.week}</span>
          <div className="pb-1">
            <p className="font-condensed text-[11px] uppercase tracking-[0.25em] text-muted">{week.phase}</p>
            <p className="font-condensed text-lg font-bold tracking-wide">{day.focus ?? day.label}</p>
          </div>
        </div>

        {week.note && (
          <div className="mb-4 rounded-r-lg border-l-[3px] border-gold bg-surface-2 px-4 py-3 text-[13px] leading-relaxed text-muted">
            {week.note}
          </div>
        )}

        {program.overview && <OverviewPanel overview={program.overview} />}

        {/* Week navigation (future weeks lock for athletes) */}
        {/* Real progress, from recorded completions — not a guess. */}
        {completionsQ.data && (
          <p className="mb-4 rounded-btn border border-border bg-surface-2 px-4 py-2 text-xs text-muted">
            {completionsQ.data.rows.length === 0 ? (
              'No sessions marked complete yet.'
            ) : (
              <>
                <span className="font-condensed uppercase tracking-[0.15em] text-success">
                  ✓ {completionsQ.data.rows.length} session
                  {completionsQ.data.rows.length === 1 ? '' : 's'} completed
                </span>
                {completionsQ.data.lastWeek != null && (
                  <> · furthest: week {completionsQ.data.lastWeek}</>
                )}
                {completionsQ.data.lastAt && (
                  <> · last {new Date(completionsQ.data.lastAt).toLocaleDateString()}</>
                )}
              </>
            )}
          </p>
        )}

        {totalWeeks > 1 && (
          <div className="mb-5">
            <p className="mb-2 font-condensed text-[11px] uppercase tracking-[0.2em] text-muted">Week</p>
            <div className="flex flex-wrap gap-2">
              {Array.from({ length: totalWeeks }, (_, i) => i + 1).map((w) => {
                const locked = w > unlockedWeek
                return (
                  <button
                    key={w}
                    disabled={locked}
                    onClick={() => !locked && pickWeek(w)}
                    className={cn(
                      'rounded-full border px-4 py-1.5 font-display text-base tracking-wide transition-all',
                      w === selWeek
                        ? 'gold-gradient border-transparent text-black shadow-gold'
                        : locked
                        ? 'cursor-not-allowed border-border text-subtle/40'
                        : 'border-border text-subtle hover:border-gold hover:text-gold'
                    )}
                  >
                    {locked ? '🔒 ' : ''}W{w}
                  </button>
                )
              })}
            </div>
            {isSelf && unlockedWeek < totalWeeks && nextUnlockAt && (
              <p className="mt-2 text-xs text-subtle">
                Week {unlockedWeek + 1} unlocks {new Date(nextUnlockAt).toLocaleDateString()}
              </p>
            )}
          </div>
        )}

        {/* Day tabs */}
        <div className="mb-5 flex gap-2 overflow-x-auto pb-1">
          {week.days.map((d, i) => {
            const complete = savedDone.has(dayKey(week.week, i + 1))
            return (
              <button
                key={i}
                onClick={() => setDayIndex(i)}
                className={cn(
                  'flex-shrink-0 rounded-full border px-5 py-2 font-display text-base tracking-wide transition-all',
                  i === dayIndex
                    ? 'gold-gradient border-transparent text-black shadow-gold'
                    : complete
                    ? 'border-success/45 text-success'
                    : 'border-border text-subtle hover:border-border-strong hover:text-gold'
                )}
              >
                {d.label}
              </button>
            )
          })}
        </div>

        {/* Exercises — supersets (A1/A2…) are bracketed together so it's clear
            they're done back-to-back. */}
        <div className="space-y-3">
          {groupSupersets(day.exercises).map((grp, gi) => {
            const cards = grp.items.map(({ ex, index: i }) => (
              <ExerciseCard
                key={`${week.week}-${dayIndex}-${i}`}
                exercise={ex}
                done={doneSet.has(i)}
                onToggle={() => toggleDone(i)}
                athleteId={id}
                programId={program.id}
                weekNum={week.week}
                dayNum={dayIndex + 1}
              />
            ))
            if (!grp.superset) return <div key={gi} className="space-y-3">{cards}</div>
            return (
              <div key={gi} className="rounded-card border border-gold/35 bg-gold/[.05] p-2">
                <div className="mb-2 flex items-center gap-2 px-1.5 pt-0.5">
                  <span className="rounded-full bg-gold/15 px-2.5 py-0.5 font-condensed text-[11px] font-bold uppercase tracking-[0.15em] text-gold">
                    ⇄ Superset {grp.letter}
                  </span>
                  <span className="text-[11px] text-muted">
                    Do {grp.items.length} back-to-back, then rest
                  </span>
                </div>
                <div className="space-y-2">{cards}</div>
              </div>
            )
          })}
        </div>

        <Button
          block
          size="lg"
          disabled={!program?.id || toggleDayComplete.isPending}
          onClick={() =>
            program?.id &&
            toggleDayComplete.mutate({
              athleteId: id,
              programId: program.id,
              week: week.week,
              day: dayIndex + 1,
              complete: !dayDone,
            })
          }
          className={cn(
            'mt-6 font-display text-xl tracking-[0.15em]',
            dayDone && 'bg-success text-black hover:bg-success'
          )}
        >
          {toggleDayComplete.isPending ? 'SAVING…' : dayDone ? '✓ DAY COMPLETE' : 'COMPLETE DAY'}
        </Button>
        {dayDone && (
          <p className="mt-2 text-center text-xs text-muted">Tap again to un-mark this day.</p>
        )}
        </>
        )}
      </main>

      {editDetails && <EditAthleteDialog athleteId={id} onClose={() => setEditDetails(false)} />}
    </div>
  )
}

function ExerciseCard({
  exercise,
  done,
  onToggle,
  athleteId,
  programId,
  weekNum,
  dayNum,
}: {
  exercise: ProgramExercise
  done: boolean
  onToggle: () => void
  athleteId: string
  programId: string
  weekNum: number
  dayNum: number
}) {
  const [expanded, setExpanded] = useState(false)
  const setCount = exercise.sets ?? 0
  const [weights, setWeights] = useState<string[]>(() => Array(setCount).fill(''))

  const { resolveId } = useExerciseLibrary()
  const exerciseId = resolveId(exercise)
  // Only fetch history once the card is opened, and only for real athletes.
  const history = useExerciseHistory(athleteId, exercise.name, expanded && !exercise.is_warmup)
  const saveSet = useSaveSet()

  // Pre-fill boxes with weights already logged for this session (so they aren't
  // lost when the card collapses or the athlete re-opens the program).
  const logged = useSessionSets(athleteId, exerciseId, programId, weekNum, dayNum, !exercise.is_warmup && setCount > 0)
  useEffect(() => {
    if (logged.data) {
      setWeights((prev) => Array.from({ length: setCount }, (_, s) => prev[s] || logged.data![s] || ''))
    }
  }, [logged.data, setCount])

  // Per-set save state, so a failed or unreadable entry is visible.
  const [setState, setSetState] = useState<Record<number, 'error' | 'invalid' | undefined>>({})
  const markSet = (s: number, v: 'error' | 'invalid' | undefined) =>
    setSetState((prev) => ({ ...prev, [s]: v }))

  const persist = (setIndex: number, raw: string) => {
    if (!isRealAthleteId(athleteId)) return // sample athletes: local only
    const parsed = parseWeight(raw)
    if (!parsed.ok) return markSet(setIndex, 'invalid')
    if (parsed.value != null) {
      setWeights((prev) => prev.map((w, i) => (i === setIndex ? String(parsed.value) : w)))
    }
    markSet(setIndex, undefined)
    saveSet.mutate(
      {
        athleteId,
        programId,
        exerciseId,
        exerciseName: exercise.name,
        week: weekNum,
        day: dayNum,
        setIndex,
        weight: parsed.value,
      },
      { onError: () => markSet(setIndex, 'error') }
    )
  }

  const detailBits = [
    exercise.load,
    exercise.tempo && `Tempo ${exercise.tempo}`,
    exercise.rest && `Rest ${exercise.rest}`,
  ].filter(Boolean) as string[]

  const setsReps =
    exercise.sets && exercise.reps
      ? `${exercise.sets} × ${exercise.reps}`
      : exercise.reps ?? ''

  if (exercise.is_warmup) {
    return (
      <div className="flex items-center gap-3 rounded-card border border-border bg-surface/60 px-4 py-3 text-muted">
        <span className="font-condensed text-sm uppercase tracking-[0.2em]">{exercise.name}</span>
        {setsReps && <span className="ml-auto text-xs">{setsReps}</span>}
      </div>
    )
  }

  return (
    <div
      className={cn(
        'overflow-hidden rounded-card border bg-gradient-to-b from-surface-2 to-surface shadow-card transition-all',
        done ? 'border-success/45' : 'border-border'
      )}
    >
      <div className="flex items-center gap-3 p-4">
        {/* Check */}
        <button
          onClick={onToggle}
          aria-label={done ? 'Mark incomplete' : 'Mark complete'}
          className={cn(
            'flex h-7 w-7 flex-shrink-0 items-center justify-center rounded-lg border-2 text-sm font-bold transition-all',
            done ? 'border-success bg-success text-black' : 'border-border-strong text-transparent hover:border-gold'
          )}
        >
          ✓
        </button>

        {/* Superset code */}
        {exercise.group && (
          <span className="flex-shrink-0 self-start rounded-md border border-border-strong bg-surface-3 px-1.5 py-0.5 font-mono text-[11px] font-bold tracking-wide text-gold">
            {exercise.group}
          </span>
        )}

        {/* Name + prescription */}
        <button onClick={() => setExpanded((v) => !v)} className="min-w-0 flex-1 text-left">
          <p className="font-condensed text-lg font-bold leading-tight">{exercise.name}</p>
          {setsReps && <p className="mt-0.5 text-[13px] font-semibold text-gold">{setsReps}</p>}
          {detailBits.length > 0 && (
            <p className="mt-0.5 text-xs text-muted">{detailBits.join(' · ')}</p>
          )}
          {exercise.notes && <p className="mt-1 text-xs italic text-subtle">{exercise.notes}</p>}
        </button>

        {exercise.video && (
          <a
            href={exercise.video}
            target="_blank"
            rel="noopener noreferrer"
            onClick={(e) => e.stopPropagation()}
            className="flex-shrink-0 rounded-full border border-danger/40 bg-danger/10 px-2.5 py-1 text-xs font-semibold text-danger hover:bg-danger/20"
            title="Watch demo video"
          >
            ▶ Watch
          </a>
        )}

        {setCount > 0 && (
          <button
            onClick={() => setExpanded((v) => !v)}
            className={cn('flex-shrink-0 text-muted transition-transform', expanded && 'rotate-180')}
            aria-label="Log weights"
          >
            ▾
          </button>
        )}
      </div>

      {/* Weight logger + history */}
      {expanded && setCount > 0 && (
        <div className="border-t border-border px-4 pb-4 pt-3 animate-fade-in">
          {/* PR / last-time summary */}
          {history.data && history.data.pr != null && (
            <div className="mb-3 flex flex-wrap items-center gap-x-4 gap-y-1 text-xs">
              <span className="font-condensed uppercase tracking-[0.15em] text-gold">
                🏆 PR {history.data.pr}kg
              </span>
              {history.data.lastWeight != null && (
                <span className="text-muted">
                  Last time: <span className="text-text">{history.data.lastWeight}kg</span>
                </span>
              )}
            </div>
          )}

          <p className="mb-2 font-condensed text-[11px] uppercase tracking-[0.2em] text-muted">
            Log weight (kg)
          </p>
          <div className="flex flex-wrap gap-2">
            {Array.from({ length: setCount }).map((_, s) => (
              <div key={s} className="flex flex-col items-center gap-1">
                <span className="text-[10px] tracking-wide text-subtle">SET {s + 1}</span>
                <input
                  inputMode="decimal"
                  value={weights[s]}
                  onChange={(e) => {
                    const next = [...weights]
                    next[s] = e.target.value
                    setWeights(next)
                  }}
                  onBlur={(e) => persist(s, e.target.value)}
                  placeholder={history.data?.lastWeight != null ? String(history.data.lastWeight) : ''}
                  className={cn(
                    'tabular h-11 w-14 rounded-lg border bg-white/[.04] text-center font-condensed text-base font-bold text-text outline-none placeholder:text-subtle/50 focus:border-gold focus:ring-2 focus:ring-gold/25',
                    setState[s] ? 'border-danger text-danger' : 'border-border-strong'
                  )}
                />
              </div>
            ))}
          </div>
          {Object.values(setState).some(Boolean) && (
            <p className="mt-2 text-xs text-danger">
              {Object.values(setState).includes('error')
                ? 'Not saved — check your connection, then tap the red box and away again.'
                : 'Enter numbers only, e.g. 60 or 62.5.'}
            </p>
          )}

          {/* History timeline */}
          <div className="mt-4">
            <p className="mb-2 font-condensed text-[11px] uppercase tracking-[0.2em] text-muted">
              History
            </p>
            {history.isLoading ? (
              <p className="text-xs text-subtle">Loading…</p>
            ) : history.data && history.data.sessions.length > 0 ? (
              <div className="space-y-1">
                {history.data.sessions
                  .slice(-6)
                  .reverse()
                  .map((sess, idx) => (
                    <div
                      key={idx}
                      className="flex items-center justify-between rounded-lg bg-white/[.02] px-3 py-1.5 text-xs"
                    >
                      <span className="text-muted">
                        {new Date(sess.date).toLocaleDateString(undefined, { day: 'numeric', month: 'short' })}
                      </span>
                      <span className="tabular text-subtle">{sess.weights.join(' · ')}kg</span>
                      <span className="tabular font-bold text-text">{sess.best}kg</span>
                    </div>
                  ))}
              </div>
            ) : (
              <p className="text-xs italic text-subtle">
                No history yet — logged weights build this record over time.
              </p>
            )}
          </div>
        </div>
      )}
    </div>
  )
}

// ── Coach-only: manage an athlete's access + week unlock ──
function CoachControls({ athleteId, onWeekJump }: { athleteId: string; onWeekJump: (w: number) => void }) {
  const { data: d } = useAthleteAccess(athleteId, true)
  const { setAccess, setUnlockedWeek, setBilling } = useAccessMutations(athleteId)
  if (!d) return null
  const paused = d.access_status === 'paused'
  const pin = (week: number | null) => {
    if (!d.assignmentId) return
    setUnlockedWeek.mutate({ assignmentId: d.assignmentId, week })
    if (week) onWeekJump(Math.min(week, d.totalWeeks))
  }
  return (
    <div className="mb-5 rounded-card border border-data/30 bg-data/[.06] p-4">
      <p className="mb-3 font-condensed text-[11px] uppercase tracking-[0.2em] text-data">Coach controls</p>
      <div className="flex flex-wrap items-center gap-x-6 gap-y-3">
        <div className="flex items-center gap-2">
          <span className="text-xs text-muted">Access</span>
          <button
            onClick={() => setAccess.mutate(paused ? 'active' : 'paused')}
            className={cn(
              'rounded-full border px-3 py-1 font-condensed text-sm font-bold uppercase tracking-wide',
              paused
                ? 'border-danger/40 bg-danger/20 text-danger'
                : 'border-success/40 bg-success/15 text-success'
            )}
          >
            {paused ? 'Paused' : 'Active'}
          </button>
        </div>

        {/* Billing: when required, Stripe drives access automatically. */}
        <div className="flex items-center gap-2">
          <span className="text-xs text-muted">Billing</span>
          <button
            onClick={() => setBilling.mutate(!d.billing_required)}
            className={cn(
              'rounded-full border px-3 py-1 font-condensed text-sm font-bold uppercase tracking-wide',
              d.billing_required
                ? 'border-data/40 bg-data/15 text-data'
                : 'border-border text-muted hover:text-gold'
            )}
          >
            {d.billing_required ? 'Stripe' : 'Off (comp)'}
          </button>
          {d.billing_required && d.subscription_status && (
            <span className="text-[11px] text-subtle">sub: {d.subscription_status}</span>
          )}
        </div>

        {d.totalWeeks > 0 && (
          <div className="flex items-center gap-2">
            <span className="text-xs text-muted">Unlocked to</span>
            <button
              onClick={() => pin(Math.max(1, d.unlockedWeek - 1))}
              className="flex h-7 w-7 items-center justify-center rounded-lg border border-border-strong text-lg leading-none hover:border-gold"
            >
              −
            </button>
            <span className="font-condensed text-lg font-bold text-gold">
              W{d.unlockedWeek}
              <span className="text-xs text-muted">/{d.totalWeeks}</span>
            </span>
            <button
              onClick={() => pin(Math.min(d.totalWeeks, d.unlockedWeek + 1))}
              className="flex h-7 w-7 items-center justify-center rounded-lg border border-border-strong text-lg leading-none hover:border-gold"
            >
              +
            </button>
            <button
              onClick={() => pin(d.weeksUnlocked === null ? d.unlockedWeek : null)}
              title={d.weeksUnlocked === null ? 'Auto on — tap to switch to manual' : 'Manual — tap for auto weekly'}
              className={cn(
                'ml-1 rounded-full border px-2.5 py-1 text-xs uppercase tracking-wide',
                d.weeksUnlocked === null
                  ? 'border-gold/40 bg-gold/15 text-gold'
                  : 'border-border text-muted hover:text-gold'
              )}
            >
              Auto {d.weeksUnlocked === null ? 'ON' : 'OFF'}
            </button>
          </div>
        )}
      </div>
      <p className="mt-2 text-[11px] text-subtle">
        {d.weeksUnlocked === null
          ? `Auto — a new week unlocks each week (currently W${d.unlockedWeek}).`
          : `Pinned to W${d.weeksUnlocked}. Tap Auto to resume weekly unlock.`}
      </p>
    </div>
  )
}
